import { useCallback, useEffect, useReducer, useRef } from "react";
import type {
  CmmdAttachment,
  CmmdForgeTaskDetail,
  CmmdProjectMilestone,
  CmmdTaskComment,
} from "@cmmd-forge/contracts";

import { createCmmdApiClient } from "~/environments/cmmdApi";
import { useTaskConversationHistory } from "./useTaskConversationHistory";
import { useTaskHistory } from "./useTaskHistory";
import { useTaskWorkSessions } from "./useTaskWorkSessions";
import { useTaskCommentMutations } from "./useTaskCommentMutations";

const api = createCmmdApiClient();

interface ProjectTaskWorkbenchState {
  readonly taskDetail: CmmdForgeTaskDetail | null;
  readonly attachments: readonly CmmdAttachment[];
  readonly milestones: readonly CmmdProjectMilestone[];
  readonly comments: readonly CmmdTaskComment[];
  readonly loading: boolean;
  readonly commenting: boolean;
  readonly error: string | null;
}

export type ProjectTaskWorkbenchAction =
  | { readonly type: "load-start" }
  | {
      readonly type: "load-ok";
      readonly milestones: readonly CmmdProjectMilestone[];
      readonly comments: readonly CmmdTaskComment[];
      readonly taskDetail: CmmdForgeTaskDetail | null;
      readonly attachments: readonly CmmdAttachment[];
      readonly error: string | null;
    }
  | { readonly type: "load-error"; readonly message: string }
  | { readonly type: "comment-start" }
  | { readonly type: "comment-ok"; readonly comment: CmmdTaskComment }
  | { readonly type: "comment-optimistic-replaced"; readonly comment: CmmdTaskComment }
  | { readonly type: "comment-optimistic-removed"; readonly commentKey: string }
  | { readonly type: "comment-replaced"; readonly comment: CmmdTaskComment }
  | { readonly type: "comment-removed"; readonly commentKey: string }
  | { readonly type: "comment-restored"; readonly comments: readonly CmmdTaskComment[] }
  | { readonly type: "comment-error"; readonly message: string }
  | { readonly type: "reset" };

type LoadedWorkbenchData = Extract<ProjectTaskWorkbenchAction, { readonly type: "load-ok" }>;
type LoadAction = Extract<ProjectTaskWorkbenchAction, { readonly type: `load-${string}` }>;
type CommentAction = Extract<ProjectTaskWorkbenchAction, { readonly type: `comment-${string}` }>;

const INITIAL: ProjectTaskWorkbenchState = {
  taskDetail: null,
  attachments: [],
  milestones: [],
  comments: [],
  loading: false,
  commenting: false,
  error: null,
};

function reducer(
  state: ProjectTaskWorkbenchState,
  action: ProjectTaskWorkbenchAction,
): ProjectTaskWorkbenchState {
  if (isLoadAction(action)) return reduceLoadAction(state, action);
  if (isCommentAction(action)) return reduceCommentAction(state, action);
  return INITIAL;
}

function isLoadAction(action: ProjectTaskWorkbenchAction): action is LoadAction {
  return action.type.startsWith("load-");
}

function isCommentAction(action: ProjectTaskWorkbenchAction): action is CommentAction {
  return action.type.startsWith("comment-");
}

function reduceLoadAction(
  state: ProjectTaskWorkbenchState,
  action: LoadAction,
): ProjectTaskWorkbenchState {
  switch (action.type) {
    case "load-start":
      return { ...INITIAL, loading: true };
    case "load-ok":
      return {
        taskDetail: action.taskDetail,
        attachments: action.attachments,
        milestones: action.milestones,
        comments: action.comments,
        loading: false,
        commenting: false,
        error: action.error,
      };
    case "load-error":
      return { ...state, loading: false, error: action.message };
  }
}

function reduceCommentAction(
  state: ProjectTaskWorkbenchState,
  action: CommentAction,
): ProjectTaskWorkbenchState {
  switch (action.type) {
    case "comment-start":
      return { ...state, commenting: true, error: null };
    case "comment-ok":
      return {
        ...state,
        commenting: false,
        comments: [...state.comments, action.comment],
      };
    case "comment-optimistic-replaced":
      return {
        ...state,
        comments: state.comments.map((comment) =>
          comment.commentKey === action.comment.commentKey ? action.comment : comment,
        ),
      };
    case "comment-optimistic-removed":
      return {
        ...state,
        comments: state.comments.filter((comment) => comment.commentKey !== action.commentKey),
      };
    case "comment-replaced":
      return {
        ...state,
        commenting: false,
        comments: state.comments.map((comment) =>
          comment.commentKey === action.comment.commentKey ? action.comment : comment,
        ),
      };
    case "comment-removed":
      return {
        ...state,
        commenting: false,
        comments: state.comments.filter((comment) => comment.commentKey !== action.commentKey),
      };
    case "comment-restored":
      return { ...state, commenting: false, comments: action.comments };
    case "comment-error":
      return { ...state, commenting: false, error: action.message };
  }
}

export function useProjectTaskWorkbenchData(projectId: string | null, taskId: number | null) {
  const conversationHistory = useTaskConversationHistory(taskId);
  const taskHistory = useTaskHistory(taskId);
  const workSessions = useTaskWorkSessions(projectId, taskId);
  const { conversationsLoaded, reloadConversations } = conversationHistory;
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const loadSequence = useRef(0);
  const commentsRef = useRef(state.comments);
  commentsRef.current = state.comments;
  const activeTaskIdRef = useRef(taskId);
  activeTaskIdRef.current = taskId;
  const editIdempotencyKeysRef = useRef(new Map<string, string>());
  const deleteIdempotencyKeysRef = useRef(new Map<string, string>());

  const reloadWorkbench = useCallback(async () => {
    const sequence = ++loadSequence.current;
    if (projectId === null || taskId === null) {
      dispatch({ type: "reset" });
      return;
    }
    dispatch({ type: "load-start" });
    try {
      const loaded = await loadWorkbenchData(projectId, taskId);
      if (sequence !== loadSequence.current) return;
      dispatch(loaded);
    } catch (cause) {
      if (sequence !== loadSequence.current) return;
      dispatch({
        type: "load-error",
        message: errorMessage(cause, "Could not load task activity."),
      });
    }
  }, [projectId, taskId]);

  useEffect(() => {
    void reloadWorkbench();
  }, [reloadWorkbench]);

  const { createComment, updateComment, deleteComment } = useTaskCommentMutations({
    taskId,
    commentsRef,
    activeTaskIdRef,
    editIdempotencyKeysRef,
    deleteIdempotencyKeysRef,
    dispatch,
    errorMessage,
  });

  const uploadAttachments = useCallback(
    async (files: readonly File[]) => {
      if (taskId === null || files.length === 0) return;
      try {
        await Promise.all(files.map((file) => api.uploadTaskAttachment(String(taskId), file)));
        await reloadWorkbench();
      } catch (cause) {
        dispatch({
          type: "load-error",
          message: errorMessage(cause, "Could not upload attachment."),
        });
        throw cause;
      }
    },
    [reloadWorkbench, taskId],
  );

  const reload = useCallback(async () => {
    await Promise.all([
      reloadWorkbench(),
      conversationsLoaded ? reloadConversations() : Promise.resolve(),
      reloadLoadedTaskWork({
        taskHistoryLoaded: taskHistory.history !== null,
        reloadTaskHistory: taskHistory.load,
        workSessionsLoaded: workSessions.loaded,
        reloadWorkSessions: workSessions.load,
      }),
    ]);
  }, [conversationsLoaded, reloadConversations, reloadWorkbench, taskHistory, workSessions]);

  const loadTaskHistory = useCallback(async () => {
    await Promise.all([taskHistory.load(), workSessions.load()]);
  }, [taskHistory, workSessions]);

  return {
    ...state,
    ...conversationHistory,
    taskHistory: taskHistory.history,
    taskHistoryLoading: taskHistory.loading,
    taskHistoryError: taskHistory.error,
    workSessions: workSessions.sessions,
    workSessionsLoaded: workSessions.loaded,
    workSessionsLoading: workSessions.loading,
    workSessionsError: workSessions.error,
    loadTaskHistory,
    reload,
    createComment,
    updateComment,
    deleteComment,
    uploadAttachments,
  };
}

async function reloadLoadedTaskWork(input: {
  readonly taskHistoryLoaded: boolean;
  readonly reloadTaskHistory: () => Promise<void>;
  readonly workSessionsLoaded: boolean;
  readonly reloadWorkSessions: () => Promise<void>;
}): Promise<void> {
  const reloads: Promise<void>[] = [];
  if (input.taskHistoryLoaded) reloads.push(input.reloadTaskHistory());
  if (input.workSessionsLoaded) reloads.push(input.reloadWorkSessions());
  await Promise.all(reloads);
}

async function loadWorkbenchData(projectId: string, taskId: number): Promise<LoadedWorkbenchData> {
  const [taskDetailResult, milestonesResult, commentsResult, attachmentsResult] =
    await Promise.allSettled([
      api.getForgeTaskDetail(taskId),
      api.listProjectMilestones(projectId),
      api.listProjectTaskComments(taskId),
      api.getTaskAttachments(String(taskId)),
    ]);
  const errors = [
    rejectedMessage(taskDetailResult, "Task planning metadata is unavailable."),
    rejectedMessage(milestonesResult, "Milestones are unavailable."),
    rejectedMessage(commentsResult, "Comments are unavailable."),
    rejectedMessage(attachmentsResult, "Attachments are unavailable."),
  ].filter((message): message is string => message !== null);
  return {
    type: "load-ok",
    taskDetail: settledValue(taskDetailResult, null),
    attachments: settledValue(attachmentsResult, []),
    milestones: settledValue(milestonesResult, []),
    comments: settledValue(commentsResult, []),
    error: errors.join(" ") || null,
  };
}

function settledValue<T>(result: PromiseSettledResult<T>, fallback: T): T {
  return result.status === "fulfilled" ? result.value : fallback;
}

function rejectedMessage<T>(result: PromiseSettledResult<T>, message: string): string | null {
  return result.status === "rejected" ? message : null;
}

function errorMessage(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message ? cause.message : fallback;
}
